import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { CreditCard, FileText, DollarSign, TrendingUp, AlertCircle, ArrowRight, Lock } from 'lucide-react';
import axios from 'axios';

const FinancialPage = () => {
  const { token, API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [financialStatus, setFinancialStatus] = useState(null);
  const [locHistory, setLocHistory] = useState([]);
  const [factoringHistory, setFactoringHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [drawDialogOpen, setDrawDialogOpen] = useState(false);
  const [factoringDialogOpen, setFactoringDialogOpen] = useState(false);
  const [drawAmount, setDrawAmount] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [status, invoicesRes] = await Promise.all([
        axios.get(`${API}/financial/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/invoices?status=sent`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFinancialStatus(status.data);
      setInvoices(invoicesRes.data.invoices);

      if (status.data.loc_enabled) {
        const locRes = await axios.get(`${API}/financial/loc/history`, { headers: { Authorization: `Bearer ${token}` } });
        setLocHistory(locRes.data.draws || []);
      }
      if (status.data.factoring_enabled) {
        const factRes = await axios.get(`${API}/financial/factoring/history`, { headers: { Authorization: `Bearer ${token}` } });
        setFactoringHistory(factRes.data.factoring_requests || []);
      }
    } catch (error) {
      console.error('Failed to fetch financial data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreditDraw = async () => {
    if (!drawAmount || parseFloat(drawAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await axios.post(`${API}/financial/loc/draw`, { amount: parseFloat(drawAmount) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Credit draw successful!');
      setDrawDialogOpen(false);
      setDrawAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process draw');
    }
  };

  const handleFactoringRequest = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('Please select at least one invoice');
      return;
    }

    try {
      await axios.post(`${API}/financial/factoring/request`, { invoice_ids: selectedInvoices }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Factoring request submitted!');
      setFactoringDialogOpen(false);
      setSelectedInvoices([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const eligibleInvoices = invoices.filter(inv => !inv.factored);

  if (loading) {
    return (
      <ClientLayout title="Financial Tools">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </ClientLayout>
    );
  }

  if (!financialStatus?.loc_enabled && !financialStatus?.factoring_enabled) {
    return (
      <ClientLayout title="Financial Tools">
        <Card className="border-gray-200 max-w-2xl mx-auto">
          <CardContent className="text-center py-12">
            <Lock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Financial Tools Not Available</h2>
            <p className="text-gray-500 mb-6">
              Your account is not currently enabled for financial tools like Line of Credit or Invoice Factoring.
              These features are available to qualifying businesses.
            </p>
            <p className="text-sm text-gray-400">
              Contact support to learn more about eligibility requirements.
            </p>
          </CardContent>
        </Card>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout title="Financial Tools">
      <div className="space-y-6">
        {/* Overview Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {financialStatus?.loc_enabled && (
            <>
              <Card className="border-gray-200" data-testid="loc-credit-limit">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Credit Limit</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${(financialStatus.credit_limit || 0).toLocaleString()}
                      </p>
                    </div>
                    <CreditCard className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200" data-testid="loc-available">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Available Credit</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        ${(financialStatus.credit_available || 0).toLocaleString()}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200" data-testid="loc-used">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Credit Used</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${(financialStatus.credit_used || 0).toLocaleString()}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Line of Credit Section */}
        {financialStatus?.loc_enabled && (
          <Card className="border-gray-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Line of Credit</CardTitle>
              </div>
              <Dialog open={drawDialogOpen} onOpenChange={setDrawDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90" data-testid="request-draw-btn">
                    Request Draw <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Request Credit Draw</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        Available: ${(financialStatus.credit_available || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">2% fee applies to each draw</p>
                    </div>
                    <div>
                      <Label>Draw Amount ($)</Label>
                      <Input
                        type="number"
                        value={drawAmount}
                        onChange={(e) => setDrawAmount(e.target.value)}
                        placeholder="1000"
                        max={financialStatus.credit_available}
                        data-testid="draw-amount-input"
                      />
                    </div>
                    {drawAmount && parseFloat(drawAmount) > 0 && (
                      <div className="text-sm text-gray-600">
                        <p>Fee (2%): ${(parseFloat(drawAmount) * 0.02).toFixed(2)}</p>
                        <p className="font-medium">Net Amount: ${(parseFloat(drawAmount) * 0.98).toFixed(2)}</p>
                      </div>
                    )}
                    <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleCreditDraw} data-testid="submit-draw-btn">
                      Submit Draw Request
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {locHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No draw history</p>
              ) : (
                <div className="space-y-2">
                  {locHistory.slice(0, 5).map((draw) => (
                    <div key={draw.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">${draw.amount.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">Fee: ${draw.fee.toFixed(2)} | Net: ${draw.net_amount.toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                          {draw.status}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(draw.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Invoice Factoring Section */}
        {financialStatus?.factoring_enabled && (
          <Card className="border-gray-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Invoice Factoring</CardTitle>
              </div>
              <Dialog open={factoringDialogOpen} onOpenChange={setFactoringDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-secondary hover:bg-secondary/90" data-testid="request-factoring-btn">
                    Factor Invoices <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Request Invoice Factoring</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-purple-800">3% factoring fee applies</p>
                      <p className="text-xs text-purple-600 mt-1">Get paid immediately on your outstanding invoices</p>
                    </div>
                    <div>
                      <Label>Select Invoices to Factor</Label>
                      <div className="max-h-48 overflow-y-auto border rounded-lg p-2 mt-2 space-y-2">
                        {eligibleInvoices.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-2">No eligible invoices</p>
                        ) : (
                          eligibleInvoices.map((invoice) => (
                            <label key={invoice.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedInvoices.includes(invoice.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedInvoices([...selectedInvoices, invoice.id]);
                                  } else {
                                    setSelectedInvoices(selectedInvoices.filter(id => id !== invoice.id));
                                  }
                                }}
                                className="rounded border-gray-300"
                              />
                              <div className="flex-1">
                                <span className="text-sm font-medium">{invoice.invoice_number}</span>
                                <span className="text-sm text-gray-500 ml-2">${invoice.total}</span>
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                    {selectedInvoices.length > 0 && (
                      <div className="text-sm text-gray-600">
                        <p>Total Value: ${eligibleInvoices.filter(i => selectedInvoices.includes(i.id)).reduce((sum, i) => sum + i.total, 0).toLocaleString()}</p>
                      </div>
                    )}
                    <Button className="w-full bg-secondary hover:bg-secondary/90" onClick={handleFactoringRequest} data-testid="submit-factoring-btn">
                      Submit Factoring Request
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {factoringHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No factoring history</p>
              ) : (
                <div className="space-y-2">
                  {factoringHistory.slice(0, 5).map((request) => (
                    <div key={request.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">${request.total_value.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">
                          Fee: ${request.factoring_fee.toFixed(2)} | Advance: ${request.advance_amount.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                          {request.status}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">About Financial Tools</p>
                <p className="text-sm text-amber-700 mt-1">
                  Line of Credit charges a 2% fee per draw. Invoice Factoring charges a 3% fee on factored invoice values.
                  These are optional tools to help manage cash flow - the core Orchard LMS software works perfectly without them.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
};

export default FinancialPage;
